import {
  ChatAnswerListSchema,
  ChatAnswerSchema,
  ComposeMailResultSchema,
  DigicsSettingsResponseSchema,
  DigicsTelegramChatsSchema,
  DigicsTestAlertResultSchema,
  DigicsTestKeyResultSchema,
  AdminUserSchema,
  CreateUserResponseSchema,
  SetPasswordLinkSchema,
  AiGenerateResponseSchema,
  AiSettingsViewSchema,
  AiStatusSchema,
  ArticleSchema,
  ContentListResponseSchema,
  DashboardSchema,
  IndustrySchema,
  InquirySchema,
  MailAddressListSchema,
  LiveChatActionResultSchema,
  LiveChatDetailSchema,
  LiveChatListSchema,
  LiveChatStaffListSchema,
  MailListSchema,
  ActivityListSchema,
  MailMessageSchema,
  MailReplySchema,
  LoginResponseSchema,
  MeResponseSchema,
  TwoFactorConfirmResponseSchema,
  TwoFactorSetupSchema,
  MediaSchema,
  MediaUsageSchema,
  NavigationSchema,
  PageSchema,
  ProductCategorySchema,
  ProductSchema,
  SitePlanSchema,
  SiteSettingsSchema,
  LocationsSchema,
  SpecReferenceSchema,
  SwatchSchema,
  TrackingDeliveriesSchema,
  TrackingTestResultSchema,
  TrackingViewSchema,
  paginated,
} from '@bak/types'
import { z } from 'zod'

/**
 * The five block/rich-text content types share one admin contract
 * (list/get/create/update/delete) — only the path, schema and labels differ.
 * Screens are generated from these definitions.
 */
export const CONTENT_RESOURCES = {
  pages: { path: '/pages', schema: PageSchema, singular: 'Page', plural: 'Pages', publicPath: (s: string) => (s === 'home' ? '/' : `/${s}`) },
  articles: { path: '/articles', schema: ArticleSchema, singular: 'Article', plural: 'Articles', publicPath: null },
  products: { path: '/products', schema: ProductSchema, singular: 'Product', plural: 'Products', publicPath: null },
  'product-categories': {
    path: '/product-categories',
    schema: ProductCategorySchema,
    singular: 'Category',
    plural: 'Categories',
    publicPath: (s: string) => `/products/${s}`,
  },
  industries: { path: '/industries', schema: IndustrySchema, singular: 'Industry', plural: 'Industries', publicPath: (s: string) => `/industries/${s}` },
  swatches: { path: '/swatches', schema: SwatchSchema, singular: 'Swatch', plural: 'Materials & Finishes', publicPath: null },
} as const

export type ContentResourceKey = keyof typeof CONTENT_RESOURCES

export const schemas = {
  me: MeResponseSchema,
  login: LoginResponseSchema,
  twoFactorSetup: TwoFactorSetupSchema,
  twoFactorConfirm: TwoFactorConfirmResponseSchema,
  sitePlan: SitePlanSchema,
  dashboard: DashboardSchema,
  contentList: ContentListResponseSchema,
  media: MediaSchema,
  mediaList: paginated(MediaSchema),
  mediaUsage: MediaUsageSchema,
  inquiry: InquirySchema,
  inquiryList: paginated(InquirySchema),
  mailList: MailListSchema,
  mailAddressList: MailAddressListSchema,
  activityList: ActivityListSchema,
  mailMessage: MailMessageSchema,
  mailReply: MailReplySchema,
  liveChatList: LiveChatListSchema,
  liveChatDetail: LiveChatDetailSchema,
  liveChatAction: LiveChatActionResultSchema,
  liveChatStaff: LiveChatStaffListSchema,
  chatAnswerList: ChatAnswerListSchema,
  chatAnswer: ChatAnswerSchema,
  digicsSettings: DigicsSettingsResponseSchema,
  digicsTestAlert: DigicsTestAlertResultSchema,
  digicsTestKey: DigicsTestKeyResultSchema,
  digicsTelegramChats: DigicsTelegramChatsSchema,
  composeMail: ComposeMailResultSchema,
  siteSettings: SiteSettingsSchema,
  tracking: TrackingViewSchema,
  trackingDeliveries: TrackingDeliveriesSchema,
  trackingTest: TrackingTestResultSchema,
  navigation: NavigationSchema,
  specReference: SpecReferenceSchema,
  locations: LocationsSchema,
  users: z.object({ items: z.array(AdminUserSchema) }),
  created: CreateUserResponseSchema,
  passwordLink: SetPasswordLinkSchema,
  aiStatus: AiStatusSchema,
  aiSettings: AiSettingsViewSchema,
  aiGenerate: AiGenerateResponseSchema,
}
